import type { JsonSchema, JsonValue } from "daedalus-core";

/**
 * OdysseyTales' door setting (ADR-122 decision 2.8, the author's answer 2): each door decides whether a character
 * in combat may go through. Daedalus stores and edits the setting as a door field and never reads it; the game
 * registers it through the adapter's `doorFields` and the map server reads it once, at load, into one bit.
 */

/**
 * The door field holding the setting.
 */
export const DOOR_COMBAT = "combat";

/**
 * The door field holding how a character enters (the author, 2026-10-29): `"walk"`, instantly on stepping onto the
 * door's cells, for open doorways and exits, or `"press"`, with the interact key and a prompt such as "Enter
 * Willowmere Inn", for shops, dungeons and rented rooms. The map server reads it once, at load, beside the combat bit.
 */
export const DOOR_ENTRY = "entry";

/**
 * How a door is entered: `Walk` on stepping onto its cells, `Press` on the interact key with its prompt showing.
 */
export const DoorEntry = { Walk: 0, Press: 1 } as const;
export type DoorEntry = (typeof DoorEntry)[keyof typeof DoorEntry];

/**
 * What a door does with a character in combat: `Refuse` leaves it standing in the door, `LetThrough` passes it as
 * anyone else. The values are the bit the map server keeps beside each door cell.
 */
export const DoorPass = { Refuse: 0, LetThrough: 1 } as const;
export type DoorPass = (typeof DoorPass)[keyof typeof DoorPass];

/**
 * The game's door fields: "Entry", Walk in (`"walk"`, the default) or Press E (`"press"`), and "In combat", Refuse
 * (`"refuse"`) or Let through (`"allow"`), Refuse by default, so a new door and a door saved before either field
 * existed walk in and refuse (ADR-122 decision 2.8).
 */
export const DOOR_FIELDS: JsonSchema = {
  type: "object",
  properties: {
    [DOOR_ENTRY]: { type: "string", title: "Entry", enum: ["walk", "press"], default: "walk" },
    [DOOR_COMBAT]: { type: "string", title: "In combat", enum: ["refuse", "allow"], default: "refuse" },
  },
  required: [DOOR_ENTRY, DOOR_COMBAT],
};

/**
 * How a door's fields say it is entered.
 *
 * @param fields The door's fields, as compiled.
 * @returns `Press` where the setting is `"press"`, `Walk` for anything else, a missing field included.
 */
export function doorEntryOf(fields: { readonly [key: string]: JsonValue }): DoorEntry {
  return fields[DOOR_ENTRY] === "press" ? DoorEntry.Press : DoorEntry.Walk;
}

/**
 * The bit a door's fields give it.
 *
 * @param fields The door's fields, as compiled.
 * @returns `LetThrough` where the setting is `"allow"`, `Refuse` for anything else, a missing field included.
 */
export function doorPassOf(fields: { readonly [key: string]: JsonValue }): DoorPass {
  return fields[DOOR_COMBAT] === "allow" ? DoorPass.LetThrough : DoorPass.Refuse;
}

/**
 * How long a refused character at a Press door waits before its refusal line is said again, in ticks: 60 at 20 a
 * second. Travel itself has no cooldown after an arrival; the transfers one character may make are capped by
 * `TRANSFER_CAP` in `TRANSFER_WINDOW_MS` (ADR-122 decision 2.7).
 */
export const DOOR_REFUSAL_REPEAT_TICKS = 60;

/**
 * Why a door left a character standing in it (ADR-122 decisions 2.8 and 5.4), as `doorRefused` names it: in combat at
 * a door that refuses fighters, in a duel, held by a Game Master, the room full, or the room not open to it (no map
 * server runs it, or it is not running a world).
 */
export const DOOR_REFUSAL_REASONS = ["combat", "duel", "held", "full", "closed"] as const;
export type DoorRefusalReason = (typeof DOOR_REFUSAL_REASONS)[number];

/**
 * What a page says about a door that left its character standing in it: a `doorRefused` reason, or `busy`, which the
 * character has made as many transfers as the cap allows in the last few seconds (`TRANSFER_CAP` in
 * `TRANSFER_WINDOW_MS`, shared by a map crossing). `busy` travels as its own message, `doorBusy`, so that a page which
 * does not know it ignores it and keeps its connection.
 */
export type DoorLine = DoorRefusalReason | "busy";

/**
 * The verb of the `interact` a page sends at a Press door, with the handle `NO_HANDLE`: the interact key while the
 * character stands in a Press door's cells (PROTOCOL 6.93.0). The server decides which door from its own position.
 */
export const DOOR_VERB = "door";

/**
 * The longest name `doorPrompt` carries: the target map's display name.
 */
export const MAX_DOOR_PROMPT_NAME_CHARS = 48;
